import { FC, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEdit } from '@fortawesome/free-solid-svg-icons';
import MasterPage from '../MasterPage/MasterPage';
import PageBase from '../../components/PageBase/PageBase';
import DropdownMenuTable from '../../components/DropdownMenuTable/DropdownMenuTable';
import ModalDefault from '../../components/ModalDefault/ModalDefault';
import { useSimulationFinancials } from '../../providers/SimulationFinancials/SimulationFinancialsProvider';
import { SimulationFinancial } from '../../models/SimulationFinancials/SimulationFinancialsModels';
import { FINANCEIRA_AUTH_CHANNEL_LABELS, FINANCEIRA_AUTH_CHANNEL_OPTIONS, FinanceiraAuthChannel } from '../../models/SimulationBank/SimulationBankModels';
import { getFinanceiraLogo } from '../../utils/FinanceiraLogoMap';
import Clt from '../../assets/icons/clt.svg';
import Inss from '../../assets/icons/inss.svg';
import './SimulationFinancials.scss';

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const SimulationFinancials: FC = () => {
    const { financials, selected, isLoading, isSaving, handleSelect, handleUpdate } = useSimulationFinancials();

    const [showModal, setShowModal] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [authChannel, setAuthChannel] = useState<FinanceiraAuthChannel | ''>('');

    useEffect(() => {
        if (!selected) return;
        setIsActive(selected.is_active);
        setAuthChannel(selected.auth_channel ?? '');
    }, [selected]);

    const hasPendingChanges = !!selected
        && (isActive !== selected.is_active || authChannel !== (selected.auth_channel ?? ''));

    const handleEdit = (financial: SimulationFinancial) => {
        handleSelect(financial);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSave = async () => {
        if (!hasPendingChanges) return;
        const ok = await handleUpdate(isActive, authChannel || null);
        if (ok) setShowModal(false);
    };

    return (
        <MasterPage>
            <div className="sim-financials">
                <PageBase
                    title="Gerenciar Financeiras"
                    subtitle="Acompanhe e altere o status e os produtos que cada financeira entrega ao integrador."
                    hasPagination={false}
                >
                    {isLoading ? (
                        <div className="sim-financials__loading">Carregando financeiras...</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-flush m-0 w-100">
                                <thead className="thead-light">
                                    <tr>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">#</th>
                                        <th className="text-uppercase opacity-7" scope="col">Financeira</th>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">Status</th>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">Produtos</th>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">Canal</th>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">Última alteração</th>
                                        <th className="text-uppercase opacity-7 text-center" scope="col">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {financials.map((financial: SimulationFinancial) => (
                                        <tr key={financial.id} className="text-sm">
                                            <td className="text-center">{financial.id}</td>
                                            <td>{financial.name}</td>
                                            <td className="text-center">
                                                <span className={financial.is_active ? 'badge bg-success' : 'badge bg-danger'}>
                                                    {financial.is_active ? 'Ativa' : 'Inativa'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="sim-financials__chips">
                                                    <span className="sim-financials__chip is-active">CLT</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {financial.auth_channel ? FINANCEIRA_AUTH_CHANNEL_LABELS[financial.auth_channel] : '-'}
                                            </td>
                                            <td className="text-center">
                                                <div>{formatDate(financial.updated_at)}</div>
                                                {financial.user_updated && (
                                                    <div className="sim-financials__updated-by">{financial.user_updated.name}</div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <DropdownMenuTable>
                                                    <Dropdown.Item
                                                        className="border-radius-md py-2"
                                                        onClick={() => handleEdit(financial)}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} className="me-2" /> Editar
                                                    </Dropdown.Item>
                                                </DropdownMenuTable>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </PageBase>
            </div>

            <ModalDefault
                className="sim-financials-modal"
                title="Editor rápido"
                icon="editSquare"
                backgroundColor="#F8F8FC"
                show={showModal}
                onClose={handleCloseModal}
                handleSubmit={handleSave}
                disabledSubmit={!hasPendingChanges || isSaving}
                showButtonSpinner={isSaving}
                buttonText="Salvar Alterações"
            >
                {selected && (
                    <div className="sim-financials__form">
                        <div className="sim-financials__fgroup">
                            <label>Financeira</label>
                            <div className="sim-financials__financeira-card">
                                {getFinanceiraLogo(selected.canonical_id) && (
                                    <img
                                        src={getFinanceiraLogo(selected.canonical_id)}
                                        alt={selected.name}
                                        className="sim-financials__financeira-logo"
                                    />
                                )}
                                <span className="sim-financials__financeira-name">{selected.name}</span>
                            </div>
                        </div>

                        <div className="sim-financials__fgroup">
                            <label>Produtos habilitados</label>
                            <div className="sim-financials__products">
                                <div className="sim-financials__product-card is-selected">
                                    <span className="sim-financials__product-check">
                                        <FontAwesomeIcon icon={faCheck} />
                                    </span>
                                    <span className="sim-financials__product-label">Consignado CLT</span>
                                    <img src={Clt} alt="CLT" className="sim-financials__product-icon" />
                                </div>
                                <div className="sim-financials__product-card">
                                    <span className="sim-financials__product-check" />
                                    <span className="sim-financials__product-label">Consignado INSS</span>
                                    <img src={Inss} alt="INSS" className="sim-financials__product-icon" />
                                </div>
                            </div>
                        </div>

                        <div className="sim-financials__fgroup">
                            <label htmlFor="sim-financials-auth-channel">Canal de autorização do cliente</label>
                            <select
                                id="sim-financials-auth-channel"
                                className="sim-financials__select"
                                value={authChannel}
                                onChange={(e) => setAuthChannel(e.target.value as FinanceiraAuthChannel | '')}
                            >
                                <option value="">Selecione...</option>
                                {FINANCEIRA_AUTH_CHANNEL_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="sim-financials__toggle-row">
                            <button
                                type="button"
                                className={`sim-financials__toggle ${isActive ? 'is-on' : ''}`}
                                onClick={() => setIsActive((v) => !v)}
                            >
                                <span className="sim-financials__toggle-knob" />
                            </button>
                            <span>{isActive ? 'Desativar financeira' : 'Ativar financeira'}</span>
                        </div>
                    </div>
                )}
            </ModalDefault>
        </MasterPage>
    );
};

export default SimulationFinancials;
